<script setup lang="ts">
    import { inject, onMounted, provide, reactive } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { FeatureService } from '@shared/directives/Feature.service';
    import Loading from '@shared/components/Loading.vue';
    import Form from '@shared/components/Form/Form.vue';
    import { FormProps } from '@shared/components/Form/Form';
    import FeatureLock from '@shared/components/FeatureLock.vue';
    import RangeSliderNative from '@shared/components/RangeSliderNative.vue';
    import ImagePreview from '@shared/components/Images/ImagePreview.vue';
    import ImageLibraryModal from '@shared/components/Images/ImageLibraryModal.vue';
    import { setupImageLibrary } from '@shared/components/Images/ImageLibrary';
    import { ImageLibraryProvide, imageLibraryProvideName } from '@shared/components/Images/types';
    import InfoPopoverButton from '@shared/components/Form/InfoPopoverButton.vue';
    import { debounce } from '@shared/utils';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import { CurrentAccountReference } from '@shared/Root/Context.access';
    import { get, update, getTimeZones, TimeZone } from './AccountInfo.access';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);

    const imageLibrary = setupImageLibrary();
    provide<ImageLibraryProvide>(imageLibraryProvideName, {
        state: imageLibrary.state,
        newSrc: imageLibrary.newSrc,
        insert: imageLibrary.insert,
        load: imageLibrary.load,
        toggleShowLibrary: imageLibrary.toggleShowLibrary,
        uploadFile: imageLibrary.uploadFile,
        uploadFromUrl: imageLibrary.uploadFromUrl,
        uploadFromBase64: imageLibrary.uploadFromBase64,
        navigateToAiEdit: imageLibrary.navigateToAiEdit,
    });

    const state = reactive<{
        isLoading: boolean;
        account: CurrentAccountReference | null;
        timeZones: TimeZone[];
        logoWidth: string;
        contactForm: FormProps;
        policyForm: FormProps;
        trackingForm: FormProps;
        retentionForm: FormProps;
        timeZoneForm: FormProps;
        pagesForm: FormProps;
    }>({
        isLoading: true,
        account: null,
        timeZones: [],
        logoWidth: '',
        contactForm: { data: {}, saveOnChange: true, hideSubmit: true },
        policyForm: { data: {}, saveOnChange: true, hideSubmit: true },
        trackingForm: { data: {}, saveOnChange: true, hideSubmit: true },
        retentionForm: { data: {}, saveOnChange: true, hideSubmit: true },
        timeZoneForm: { data: {}, saveOnChange: true, hideSubmit: true },
        pagesForm: { data: {}, saveOnChange: true, hideSubmit: true },
    });

    onMounted(async () => {
        const [account, timeZones] = await Promise.all([get(), getTimeZones()]);

        state.account = account;
        state.timeZones = timeZones;
        state.logoWidth = account.LogoWidth ? account.LogoWidth + 'px' : '';

        initForms();

        state.isLoading = false;
    });

    function initForms(): void {
        const account = state.account!;

        state.contactForm.data = {
            PublicName: {
                modelValue: account.PublicName,
                input: {
                    label: $Resources.AccountSettingsContactCompanyLabel,
                    placeholder: $Resources.AccountSettingsContactCompanyPlaceholder,
                    maxlength: 255,
                },
            },
            Email: {
                modelValue: account.Email,
                input: {
                    label: $Resources.AccountSettingsContactEmailLabel,
                    placeholder: $Resources.AccountSettingsContactEmailPlaceholder,
                    maxlength: 100,
                    type: 'email',
                },
            },
            Phone: {
                modelValue: account.Phone,
                input: {
                    label: $Resources.AccountSettingsContactPhoneLabel,
                    placeholder: $Resources.AccountSettingsContactPhonePlaceholder,
                    maxlength: 20,
                },
            },
            WebUrl: {
                modelValue: account.WebUrl,
                required: true,
                input: {
                    label: $Resources.AccountSettingsContactWebsiteLabel,
                    placeholder: $Resources.AccountSettingsContactWebsitePlaceholder,
                    type: 'text',
                    maxlength: 255,
                },
            },
        };

        state.policyForm.data = {
            PrivacyUrl: {
                modelValue: account.PrivacyUrl,
                input: {
                    label: $Resources.AccountSettingsContactPolicyLabel,
                    placeholder: $Resources.AccountSettingsContactPolicyPlaceholder,
                    maxlength: 255,
                },
            },
        };

        state.trackingForm.data = {
            GoogleTagManagerId: {
                modelValue: account.GoogleTagManagerId,
                input: {
                    label: $Resources.GoogleTagManagerIdLabel,
                    placeholder: $Resources.GoogleTagManagerIdPlaceholder,
                    maxlength: 50,
                },
            },
        };

        state.retentionForm.data = {
            SensitiveQuestionRententionDays: {
                modelValue: account.SensitiveQuestionRententionDays,
                input: {
                    label: $Resources.AccountRetentionLabel,
                    type: 'number',
                    min: 0,
                },
            },
            DeletedContactsRetentionDays: {
                modelValue: account.DeletedContactsRetentionDays,
                input: {
                    label: $Resources.AccountDeletedContactsRetentionDays,
                    maxlength: 255,
                },
            },
        };

        state.timeZoneForm.data = {
            TimeZone: {
                modelValue: account.TimeZone,
                select: {
                    label: $Resources.AccountDefaultTimeZone,
                    options: state.timeZones.map((timeZone) => ({ label: timeZone.Name, value: timeZone.TimeZoneId })),
                },
            },
        };

        if (featureAccess.hasFeature('PagesPreview')) {
            state.pagesForm.data = {
                DefaultDomain: {
                    modelValue: account.DefaultDomain,
                    input: {
                        label: $Resources.DefaultDomain,
                        maxlength: 50,
                    },
                },
            };
        }
    }

    function propertyLabel(key: string, fallback: string): string {
        const resources = $Resources as unknown as Record<string, string>;
        return resources['PropertyAccount' + key + 'Label'] || resources['propertyAccount' + key + 'Label'] || fallback;
    }

    async function saveProperty(key: string, value: any, fallbackLabel: string): Promise<void> {
        if (!state.account) return;

        try {
            await update(key, value);
            (state.account as any)[key] = value;
            bsAlert({ type: AlertTypes.Success, title: $Resources.PropertyUpdatedLabel, content: propertyLabel(key, fallbackLabel) });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.PropertyNotUpdatedLabel, content: propertyLabel(key, fallbackLabel) });
        }
    }

    function onLogoUrlChange(newValue: string): void {
        if (state.account) state.account.LogoUrl = newValue;
        saveProperty('LogoUrl', newValue, $Resources.AccountSettingsLogotypeHeading);
    }

    function onLogoWidthChange(newValue: string): void {
        state.logoWidth = newValue;
        debounce(() => saveProperty('LogoWidth', parseFloat(newValue), $Resources.AccountSettingsLogotypeWidth), { delay: 500 });
    }
</script>

<template>
    <Loading :show="state.isLoading" loading-style="progress-bar" fixed></Loading>

    <div class="max-700" v-if="state.account">
        <h1 class="h2 m-lg-b">
            {{ $Resources.AccountInfoTabSettings }}
            <small>({{ state.account.Name }})</small>
        </h1>

        <h2 class="h4">{{ $Resources.AccountSettingsContactHeading }}</h2>
        <p
            class="m-lg-b"
            v-html="$Resources.AccountPageTabsAccountTextWithLink.replace('{{PreviewPreferenceCenterUrl}}', state.account?.PreviewPreferenceCenterUrl)"></p>

        <Form
            class="wrapper-grid"
            :data="state.contactForm.data"
            :saveOnChange="true"
            :hideSubmit="true"
            @update:modelValue="(key, newVal) => (state.contactForm.data[key].modelValue = newVal)"
            @onSubmitSingleInput="(key, newVal) => saveProperty(key, newVal, $Resources.AccountSettingsContactHeading)"></Form>

        <h2 class="h4">
            {{ $Resources.AccountSettingsLogotypeHeading }}
            <InfoPopoverButton
                :heading="$Resources.AccountSettingsLogotypeHeading"
                :info-text="$Resources.AccountSettingsLogotypeInfoText"
                icon-classes="text-md" />
        </h2>

        <ImagePreview
            class="img-preview-no-mh"
            :imageSrc="state.account.LogoUrl"
            @update:imageSrc="onLogoUrlChange"
            :preview-size="{
                width: state.logoWidth ? parseFloat(state.logoWidth) : 200,
            }"></ImagePreview>

        <RangeSliderNative
            class="form-group"
            :modelValue="state.logoWidth"
            :label="$Resources.AccountSettingsLogotypeWidth"
            :min="40"
            :max="600"
            :step="10"
            suffix="px"
            @update:modelValue="onLogoWidthChange"></RangeSliderNative>

        <h2 class="h4">{{ $Resources.AccountSettingsContactPolicyHeading }}</h2>

        <Form
            class="wrapper-grid"
            :data="state.policyForm.data"
            :saveOnChange="true"
            :hideSubmit="true"
            @update:modelValue="(key, newVal) => (state.policyForm.data[key].modelValue = newVal)"
            @onSubmitSingleInput="(key, newVal) => saveProperty(key, newVal, $Resources.AccountSettingsContactPolicyHeading)"></Form>

        <h2 class="h4">{{ $Resources.AccountTrackingHeading }}</h2>

        <FeatureLock feature="Forms.GoogleTagManager" :hideWhenLocked="false">
            <Form
                class="wrapper-grid"
                :data="state.trackingForm.data"
                :saveOnChange="true"
                :hideSubmit="true"
                @update:modelValue="(key, newVal) => (state.trackingForm.data[key].modelValue = newVal)"
                @onSubmitSingleInput="(key, newVal) => saveProperty(key, newVal, $Resources.AccountTrackingHeading)"></Form>
        </FeatureLock>

        <h2 class="h4">{{ $Resources.AccountRetentionHeading }}</h2>
        <p>{{ $Resources.AccountRetentionText }}</p>

        <Form
            class="wrapper-grid"
            :data="state.retentionForm.data"
            :saveOnChange="true"
            :hideSubmit="true"
            @update:modelValue="(key, newVal) => (state.retentionForm.data[key].modelValue = newVal)"
            @onSubmitSingleInput="(key, newVal) => saveProperty(key, newVal, $Resources.AccountRetentionHeading)"></Form>

        <h2 class="h4">{{ $Resources.AccountDateAndTimeZoneHeading }}</h2>

        <Form
            class="wrapper-grid"
            :data="state.timeZoneForm.data"
            :saveOnChange="true"
            :hideSubmit="true"
            @update:modelValue="(key, newVal) => (state.timeZoneForm.data[key].modelValue = newVal)"
            @onSubmitSingleInput="(key, newVal) => saveProperty(key, newVal, $Resources.AccountDateAndTimeZoneHeading)"></Form>

        <template v-if="featureAccess.hasFeature('PagesPreview')">
            <h2 class="h4">{{ $Resources.Pages }}</h2>

            <Form
                class="wrapper-grid"
                :data="state.pagesForm.data"
                :saveOnChange="true"
                :hideSubmit="true"
                @update:modelValue="(key, newVal) => (state.pagesForm.data[key].modelValue = newVal)"
                @onSubmitSingleInput="(key, newVal) => saveProperty(key, newVal, $Resources.Pages)"></Form>
        </template>
    </div>

    <ImageLibraryModal />
</template>
